import { useCallback, useState } from 'react'
import type { JSX } from 'react'
import { useSplitEnabled } from '@buffer-mono/features'
import { Lightbox, Text, useResizeObserver } from '@buffer-mono/popcorn'
import type { LightboxProps } from '@buffer-mono/popcorn'

import { InstagramMusicVideoPlayer } from '~publish/components/Composer/InstagramMusic/InstagramMusicVideoPlayer'
import { useInstagramSelectedAudioPreview } from '~publish/components/Composer/InstagramMusic/hooks/useInstagramSelectedAudioPreview'
import { usePostData } from '../PostCardContext'

import styles from './PostMediaLightbox.module.css'

type Soundtrack = {
  id: string
  audioVolume: number
  videoVolume: number
}

const InstagramMusicSlide = ({
  source,
  poster,
  width,
  height,
  rect,
  audio,
  channelId,
}: {
  source: string
  poster?: string
  width?: number
  height?: number
  rect: { width: number; height: number }
  audio: Soundtrack
  channelId: string
}): JSX.Element => {
  const preview = useInstagramSelectedAudioPreview(
    { audio_id: audio.id },
    channelId,
  )
  const [playbackFailed, setPlaybackFailed] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)
  const handleVideoError = useCallback(() => setVideoFailed(true), [])
  const [messageRef, messageSize] = useResizeObserver<HTMLDivElement>({
    box: 'border-box',
  })
  const message = videoFailed
    ? 'Video preview unavailable.'
    : preview.errorMessage || playbackFailed
      ? 'Music preview unavailable. You can still play the video.'
      : null
  const aspectRatio = width && height ? width / height : 9 / 16
  const availableHeight = Math.max(
    0,
    rect.height - (message ? (messageSize.height ?? 0) : 0),
  )
  const displayWidth = Math.min(
    rect.width,
    Math.min(500, availableHeight) * aspectRatio,
  )

  return (
    <div className={styles.slide} style={{ width: rect.width }}>
      <div style={{ width: displayWidth, height: displayWidth / aspectRatio }}>
        <InstagramMusicVideoPlayer
          video={{ url: source, thumbnail: poster ?? '', width, height }}
          nativeControls
          className={styles.player}
          soundtrackSrc={preview.dataUrl}
          soundtrackVolume={audio.audioVolume}
          videoVolume={audio.videoVolume}
          onPlaybackErrorChange={setPlaybackFailed}
          onAuditionError={handleVideoError}
        />
      </div>
      <div ref={messageRef} className={message ? styles.message : undefined}>
        {message && (
          <output>
            <Text color="inverted" size="sm">
              {message}
            </Text>
          </output>
        )}
      </div>
    </div>
  )
}

export const PostMediaLightbox = ({
  slides,
  children,
}: Pick<LightboxProps, 'slides' | 'children'>): JSX.Element => {
  const post = usePostData()
  const flag = useSplitEnabled('instagram-music')
  const audio =
    flag.isReady &&
    flag.isEnabled &&
    post.via !== 'network' &&
    post.channel.service === 'instagram' &&
    post.schedulingType === 'automatic' &&
    post.metadata?.__typename === 'InstagramPostMetadata' &&
    post.metadata.type === 'reel'
      ? post.metadata.audio
      : null

  return (
    <Lightbox
      key={post.id}
      slides={slides}
      render={
        audio
          ? {
              slide: ({ slide, offset, rect }) => {
                if (slide.type !== 'video' || offset !== 0) return undefined
                const source = slide.sources[0]?.src
                if (!source) return undefined
                return (
                  <InstagramMusicSlide
                    key={`${post.id}:${post.channel.id}:${source}:${audio.id}`}
                    source={source}
                    poster={slide.poster}
                    width={slide.width}
                    height={slide.height}
                    rect={rect}
                    audio={audio}
                    channelId={post.channel.id}
                  />
                )
              },
            }
          : undefined
      }
    >
      {children}
    </Lightbox>
  )
}
